import { Fragment, useEffect, useState } from "react";
import {
  Input,
  Row,
  Col,
  Modal,
  Button,
  ModalHeader,
  ModalBody,
  Tooltip,
  UncontrolledTooltip,
} from "reactstrap";
import { statusChipStyle } from "../Table/CSOPartnershipTable";
import MessageIcon from "../assets/icon/message.png";
import ReactTable from "react-table";
import { useLazyQuery, useQuery, useMutation } from "@apollo/client";
import { GET_COMPARISON_DATA, GET_CSO_LIST } from "../queries";
import CSOsFinanceRadar from "./CsosFinanceRadar";
import CsosFinanceColumns from "./CsosFinanceColumn";
import { parseArray } from "../helpers";
import styles from "./index.module.scss";
import { DELETE_CSO_WISH, DELETE_SUPPORTER_STATUS } from "../mutations";
import animationLogo from "../../../../assets/utils/images/animetion_logo.gif";
import { sendGAEvent } from "../../../../utils";
import DissolveBadge from "../../../../components/DissolveBadge";
import { CHECK_USER_STATUS } from "../../../../queries";
import { useUserEmail } from "../../../../Layout/AppMain/LoginStatusContext";

const defaultData = {
  year: [2021, 2022, 2023],
  収益規模: [
    {
      cso_name: "選ばれたCSO",
      data: [0, 0, 0],
    },
  ],
  "寄付・会費率": [
    {
      cso_name: "選ばれたCSO",
      data: [0, 0, 0],
    },
  ],
  事業収入比率: [
    {
      cso_name: "選ばれたCSO",
      data: [0, 0, 0],
    },
  ],
  社会的支援収入比率: [
    {
      cso_name: "選ばれたCSO",
      data: [0, 0, 0],
    },
  ],
  収入多様性指標: [
    {
      cso_name: "選ばれたCSO",
      data: [0, 0, 0],
    },
  ],
  管理費比率: [
    {
      cso_name: "選ばれたCSO",
      data: [0, 0, 0],
    },
  ],
  "正味財産・収益比率": [
    {
      cso_name: "選ばれたCSO",
      data: [0, 0, 0],
    },
  ],
  収益率: [
    {
      cso_name: "選ばれたCSO",
      data: [0, 0, 0],
    },
  ],
};

const bgColor = {
  1: "#94DCE9",
  2: "#8088E4",
  3: "#FF9898",
  4: "#98CF8E",
};

const moveLastPropertyToBeginning = (obj) => {
  const keys = Object.keys(obj); // Get an array of the object's keys
  const lastKey = keys.pop(); // Remove the last key from the array and store it
  keys.unshift(lastKey); // Add the last key to the beginning of the array
  const newObj = {};
  keys.forEach((key) => {
    newObj[key] = obj[key]; // Create a new object using the reordered keys
  });
  return newObj;
};

const removeKeyFromObject = (obj, keyToRemove) => {
  const newObj = {};
  Object.keys(obj).forEach((key) => {
    if (key !== keyToRemove) {
      newObj[key] = obj[key];
    }
  });
  return newObj;
};

const CompareCSO = (props) => {
  const { activeKey } = props;
  const [checkedOrgs, setCheckedOrgs] = useState([]);
  const [comparedOrgs, setComparedOrgs] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeColumnFilter, setActiveColumnFilter] = useState(0);
  const [longestSDGs, setLongestSDGs] = useState(3);

  const {
    data: rawData,
    loading: loadingCso,
    refetch: refetchCsoList,
  } = useQuery(GET_CSO_LIST);

  useEffect(() => {
    localStorage.setItem("tabActive", activeKey);
    sendGAEvent(
      "pageview",
      "/#/dashboards/organization-partnership/competition_tab"
    );
  }, [activeKey]);

  const [
    deleteCsoWish,
    { data: deletedCsoWish, loading: loadingDeleteCsoWish },
  ] = useMutation(DELETE_CSO_WISH);
  const [
    deleteCollaborationStatus,
    {
      data: deletedCollaborationStatus,
      loading: loadingDeleteCollaborationStatus,
    },
  ] = useMutation(DELETE_SUPPORTER_STATUS);

  const handleDeleteCsoWish = () => {
    if (selectedOrg?.__typename === "CollaborationStatusNode") {
      deleteCollaborationStatus({
        variables: {
          supporterStatusId: selectedOrg.id,
        },
        onCompleted: () => {
          refetchCsoList();
        },
      });
    } else {
      deleteCsoWish({
        variables: {
          csoWishId: selectedOrg.id,
        },
        onCompleted: () => {
          refetchCsoList();
        },
      });
    }
    setSelectedOrg(null);
    setDeleteModalOpen(false);
  };

  const toggle = () => setDeleteModalOpen(!deleteModalOpen);
  const data = rawData
    ? [
        ...rawData?.myCollaborationStatusesAsSupporter?.edges?.map((edge) => {
          const length = parseArray(edge.node.cso.sdgsImages).filter(
            (v) => v !== ""
          ).length;
          if (length > longestSDGs) {
            setLongestSDGs(length);
          }
          return edge.node;
        }),
        ...rawData?.myCsoWishes?.edges?.map((edge) => {
          const length = parseArray(edge.node.cso.sdgsImages).filter(
            (v) => v !== ""
          ).length;
          if (length > longestSDGs) {
            setLongestSDGs(length);
          }
          return {
            ...edge.node,
            readableSupporterStatus: "候補",
          };
        }),
      ]
    : [];

  const [
    getComparisonData,
    { data: rawComparisonData, loading: loadingComparisonData },
  ] = useLazyQuery(GET_COMPARISON_DATA);
  const comparisonData = rawComparisonData
    ? JSON.parse(rawComparisonData?.compareCsoData?.data)
    : {};

  const handleCheckRow = (cso) => {
    if (checkedOrgs.map((item) => item.id).includes(cso.id))
      return setCheckedOrgs(checkedOrgs.filter((item) => item.id !== cso.id));
    return setCheckedOrgs((prev) => [...prev, cso]);
  };

  const { data: checkUserStatus } = useQuery(CHECK_USER_STATUS, {
    variables: {
      email: useUserEmail(),
    },
  });

  const canEdit =
    checkUserStatus?.allUsers?.edges[0]?.node?.supporterRole === "can_edit" ||
    checkUserStatus?.allUsers?.edges[0]?.node?.supporterRole === "CAN_EDIT";

  const comparisonTableColumns = [
    {
      Header: "選択",
      accessor: "select",
      width: 50,
      Cell: ({ row }) => {
        return (
          <div style={{ textAlign: "center", width: "70px" }}>
            <Input
              onChange={() => {
                handleCheckRow(row._original);
              }}
              checked={checkedOrgs
                .map((item) => item.id)
                .includes(row._original.id)}
              style={{ position: "relative", left: 10 }}
              type="checkbox"
            />
          </div>
        );
      },
    },
    {
      Header: "ステータス",
      accessor: "status",
      width: 110,
      Cell: ({ row }) => {
        const status = row._original.readableSupporterStatus;
        const hasNewMessage = row?._original?.supporterHasNewMessage;
        if (!status) return <></>;
        return (
          <div
            style={{
              ...(statusChipStyle[status] || statusChipStyle["default"]),
              fontSize: "9px",
              padding: "4px 7px",
              borderRadius: "18px",
              whiteSpace: "nowrap",
              margin: "auto",
              position: "relative",
            }}
          >
            {status}
            {hasNewMessage && (
              <div
                style={{
                  position: "absolute",
                  top: "-3px",
                  right: "0",
                  width: "10px",
                  height: "10px",
                  borderRadius: "50%",
                  backgroundColor: "#FF0000",
                }}
              />
            )}
          </div>
        );
      },
    },
    {
      Header: "組織名",
      accessor: "name",
      minWidth: 325,
      Cell: ({ row }) => {
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            {row._original.cso.name}
            {row._original.cso.isDissolve && <DissolveBadge />}
          </div>
        );
      },
    },
    {
      Header: "活動国",
      accessor: "country",
      minWidth: 500,
      Cell: ({ row }) => {
        return (
          <div
            style={{
              textAlign: "center",
              width: "100%",
            }}
          >
            {row?._original?.cso?.csocollaborationneeds?.countries?.edges
              ?.slice(0, 8)
              ?.map((edge) => edge?.node?.nameJp)
              .join(", ")}
            {row?._original?.cso?.csocollaborationneeds?.countries?.edges
              ?.length > 8 && "..."}
          </div>
        );
        //   return row._original.cso.country;
      },
    },
    {
      Header: () => {
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              justifyContent: "center",
            }}
            className="hatena_box"
          >
            <span>情報度</span>
            <span
              style={{
                width: "20px",
                height: "20px",
                fontSize: "13px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "50%",
                border: "1px solid #adaeaf",
                color: "#adaeaf",
              }}
              id="quality"
            >
              ?
            </span>
            <UncontrolledTooltip target="quality" placement="top">
              <div
                style={{
                  textAlign: "left",
                }}
              >
                <p>
                  星の数はSocial
                  map上に掲載されている組織情報の充実度によって自動で変わります。
                </p>
                <p>星1：「組織概要」の記載</p>
                <p>星2：「活動国」「財務データ」の記載</p>
                <p>星3：「ミッションと連携方針」の記載</p>
              </div>
            </UncontrolledTooltip>
          </div>
        );
      },
      accessor: "cso.quality",
      minWidth: 119,
      Cell: ({ row }) => {
        const public_info_quality = row?._original?.cso?.publicInformationScore;
        const max_quality = 3;

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "2px",
              width: "100px",
            }}
          >
            {new Array(public_info_quality).fill(0).map((item, idx) => {
              return (
                <div key={idx}>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9.04893 3.92731C9.34828 3.006 10.6517 3.006 10.951 3.92731L12.0206 7.21911C12.1545 7.63113 12.5384 7.91009 12.9717 7.91009H16.4329C17.4016 7.91009 17.8044 9.14971 17.0207 9.71911L14.2205 11.7536C13.87 12.0082 13.7233 12.4596 13.8572 12.8716L14.9268 16.1634C15.2261 17.0847 14.1717 17.8508 13.3879 17.2814L10.5878 15.247C10.2373 14.9923 9.76269 14.9923 9.4122 15.247L6.61203 17.2814C5.82832 17.8508 4.77384 17.0847 5.07319 16.1634L6.14276 12.8716C6.27663 12.4596 6.12997 12.0082 5.77949 11.7536L2.97932 9.71911C2.1956 9.14971 2.59838 7.91009 3.5671 7.91009H7.0283C7.46153 7.91009 7.84548 7.63113 7.97936 7.21911L9.04893 3.92731Z"
                      fill="#646262"
                    />
                  </svg>
                </div>
              );
            })}
            {new Array(max_quality - public_info_quality)
              .fill(0)
              .map((item, idx) => {
                return (
                  <div key={idx}>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g clip-path="url(#clip0_270_3987)">
                        <path
                          d="M9.04896 2.92664C9.34833 2.00537 10.6517 2.00538 10.951 2.92664L12.0208 6.21864C12.1547 6.63063 12.5386 6.90957 12.9718 6.90958L16.4333 6.90971C17.402 6.90975 17.8047 8.1493 17.0211 8.71871L14.2208 10.7534C13.8703 11.008 13.7237 11.4594 13.8575 11.8714L14.927 15.1635C15.2263 16.0847 14.1719 16.8508 13.3882 16.2815L10.5878 14.247C10.2373 13.9924 9.76272 13.9924 9.41225 14.247L6.61179 16.2815C5.82809 16.8508 4.77367 16.0847 5.07297 15.1635L6.14249 11.8714C6.27634 11.4594 6.1297 11.008 5.77924 10.7534L2.97894 8.71871C2.19528 8.1493 2.59804 6.90975 3.56672 6.90971L7.02818 6.90958C7.46137 6.90957 7.8453 6.63063 7.97918 6.21864L9.04896 2.92664Z"
                          stroke="#646262"
                          stroke-width="2"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_270_3987">
                          <rect width="20" height="20" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </div>
                );
              })}
          </div>
        );
      },
    },
    // {
    //   Header: () => {
    //     return (
    //       <div
    //         style={{
    //           display: "flex",
    //           alignItems: "center",
    //           gap: "8px",
    //           justifyContent: "center",
    //         }}
    //         className="hatena_box"
    //       >
    //         <span>信頼度</span>
    //         <span
    //           style={{
    //             width: "20px",
    //             height: "20px",
    //             fontSize: "13px",
    //             display: "flex",
    //             alignItems: "center",
    //             justifyContent: "center",
    //             borderRadius: "50%",
    //             border: "1px solid #adaeaf",
    //             color: "#adaeaf",
    //           }}
    //           id="reliability"
    //         >
    //           ?
    //         </span>
    //         <UncontrolledTooltip target="reliability" placement="top">
    //           <div
    //             style={{
    //               textAlign: "left",
    //             }}
    //           >
    //             <p>
    //               月の数はSocial
    //               map上に掲載されている組織情報の充実度によって自動で変わります。
    //             </p>
    //             <p>月1：「ネットワーク組織」の加盟</p>
    //             <p>月2：「第三者認証」の実施</p>
    //             <p>月3： 現在基準設定中</p>
    //           </div>
    //         </UncontrolledTooltip>
    //       </div>
    //     );
    //   },
    //   accessor: "cso.reliability",
    //   minWidth: 119,
    //   Cell: ({ row }) => {
    //     // RELIABILITY COLUMN
    //     const reliability_score = row?._original?.cso?.reliabilityScore;
    //     const max_reliability = 3;
    //     return (
    //       <div
    //         style={{
    //           display: "flex",
    //           alignItems: "center",
    //           justifyContent: "center",
    //           gap: "2px",
    //           width: "100px",
    //         }}
    //       >
    //         {new Array(reliability_score).fill(0).map((item, idx) => {
    //           return (
    //             <div key={idx}>
    //               <svg
    //                 width="20"
    //                 height="20"
    //                 viewBox="0 0 20 20"
    //                 fill="none"
    //                 xmlns="http://www.w3.org/2000/svg"
    //               >
    //                 <path
    //                   d="M17.2929 13.2933C16.2886 13.7474 15.1738 14.0003 14 14.0003C9.58172 14.0003 6 10.4185 6 6.00026C6 4.82629 6.25287 3.71138 6.70712 2.70703C3.93137 3.96228 2 6.75563 2 10.0001C2 14.4183 5.58172 18.0001 10 18.0001C13.2443 18.0001 16.0376 16.0689 17.2929 13.2933Z"
    //                   fill="#646262"
    //                 />
    //               </svg>
    //             </div>
    //           );
    //         })}
    //         {new Array(max_reliability - reliability_score)
    //           .fill(0)
    //           .map((item, idx) => {
    //             return (
    //               <div key={idx}>
    //                 <svg
    //                   width="20"
    //                   height="20"
    //                   viewBox="0 0 20 20"
    //                   fill="none"
    //                   xmlns="http://www.w3.org/2000/svg"
    //                 >
    //                   <path
    //                     d="M16.9618 12.796C16.098 13.1432 15.1546 13.3342 14.1667 13.3342C10.0245 13.3342 6.66667 9.97636 6.66667 5.83422C6.66667 4.84624 6.8577 3.90288 7.20484 3.03906C4.44707 4.14733 2.5 6.84673 2.5 10.0009C2.5 14.143 5.85786 17.5009 10 17.5009C13.1542 17.5009 15.8536 15.5538 16.9618 12.796Z"
    //                     stroke="#646262"
    //                     stroke-width="2"
    //                     stroke-linecap="round"
    //                     stroke-linejoin="round"
    //                   />
    //                 </svg>
    //               </div>
    //             );
    //           })}
    //       </div>
    //     );
    //   },
    // },
    {
      Header: "SDGs",
      accessor: "sdgs",
      width: longestSDGs * 25 + 20,
      Cell: ({ row }) => {
        const sgdsLogos = parseArray(row._original.cso.sdgsImages).filter(
          (item) => item !== ""
        );
        return (
          <div
            className="d-flex w-100"
            style={{
              maxWidth: "320px",
              flexWrap: "wrap",
            }}
          >
            {sgdsLogos.map((logo) => {
              return (
                <div key={logo} className="mr-1">
                  <img
                    src={logo}
                    alt="sdg"
                    style={{ width: "30px", height: "30px" }}
                  />
                </div>
              );
            })}
          </div>
        );
      },
    },
    {
      Header: "活動分野",
      accessor: "field",
      minWidth: 250,
      Cell: ({ row }) => {
        return (
          row._original.cso?.csocollaborationneeds?.activityFields?.edges
            ?.map((edge) => {
              return edge?.node?.name;
            })
            .join(", ") ?? ""
        );
      },
    },
    {
      Header: "団体規模",
      accessor: "scale",
      width: 200,
      Cell: ({ row }) => {
        return row._original.cso.revenueCategory.scale;
      },
    },
    {
      Header: "メッセージ",
      accessor: "message",
      width: 100,
      Cell: ({ row }) => {
        return (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              width: "100px",
            }}
          >
            <i className="pe-7s-mail" style={{ fontSize: "20px" }}></i>
          </div>
        );
      },
    },
    {
      Header: "",
      width: 45,
      Cell: ({ row }) => {
        return (
          <>
            {canEdit && (
              <button
                type="button"
                style={{
                  backgroundColor: "transparent",
                  border: "none",
                  width: "",
                  fontSize: "12px",
                }}
                onClick={() => {
                  setSelectedOrg(row._original);
                  setDeleteModalOpen(true);
                }}
              >
                &#x2715;
              </button>
            )}
          </>
        );
      },
    },
  ];

  return (
    <Fragment>
      <Row
        style={{
          marginTop: "16px",
        }}
      >
        <Col
          xs={12}
          lg={2}
          style={{
            display: "grid",
            gridTemplateColumns: "150px 1fr",
            marginBottom: "12px",
          }}
        >
          <div
            style={{
              marginRight: "16px",
              width: "100%",
              paddingRight: "16px",
            }}
          >
            <button
              style={{
                padding: "9px 24px",
                backgroundColor: "#134693",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                width: "100%",
              }}
              onClick={() => {
                getComparisonData({
                  variables: {
                    csoIdList: checkedOrgs.map((item) => item.cso.id),
                  },
                });
                setComparedOrgs(checkedOrgs);
                setCheckedOrgs([]);
              }}
            >
              比較する
            </button>
          </div>
        </Col>
        <Col xs={12} lg={10}>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
            }}
          >
            {comparedOrgs.map((item, idx) => {
              return (
                <button
                  key={item.id}
                  style={{
                    padding: "9px 16px",
                    backgroundColor: bgColor[(idx % 4) + 1],
                    marginRight: "8px",
                    marginBottom: "6px",
                    borderRadius: "999px",
                    color: "#fff",
                    border: "none",
                  }}
                  onClick={() => {
                    setComparedOrgs(
                      comparedOrgs.filter((org) => org.id !== item.id)
                    );
                    getComparisonData({
                      variables: {
                        csoIdList: comparedOrgs
                          .filter((org) => org.id !== item.id)
                          .map((org) => org.cso.id),
                      },
                    });
                  }}
                >
                  <span>{item.cso.name}</span>
                  <span style={{ marginLeft: "6px" }}>&#10005;</span>
                </button>
              );
            })}
          </div>
        </Col>
      </Row>
      <Row
        className="mt-3"
        style={{
          overflowX: "auto",
        }}
      >
        <Col xs={12}>
          {loadingCso ? (
            <div
              style={{
                width: "100%",
                height: "500px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  width: "150px",
                  height: "150px",
                }}
              >
                <img
                  src={animationLogo}
                  alt="loading"
                  style={{
                    width: "80px",
                    height: "80px",
                  }}
                />
              </div>
            </div>
          ) : (
            <ReactTable
              loading={loadingCso}
              defaultPageSize={5}
              pageSizeOptions={[5, 10, 15, 20, 25]}
              className="-striped -highlight"
              previousText="前へ"
              nextText="次へ"
              pageText=""
              ofText="of"
              rowsText=""
              style={{ cursor: "pointer" }}
              data={data}
              columns={comparisonTableColumns}
            />
          )}
        </Col>
      </Row>
      {/* {rawComparisonData && ( */}
      <Row>
        <Col
          xs={12}
          lg={6}
          style={{
            marginTop: "16px",
          }}
        >
          <div
            style={{
              border: "1px solid #E5E5E5",
              padding: "16px",
            }}
          >
            <div
              style={{
                fontWeight: "bold",
              }}
            >
              最新年度データ
            </div>
            {loadingComparisonData ? (
              <>
                <div
                  style={{
                    width: "100%",
                    height: "300px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <img
                    src={animationLogo}
                    alt="loading"
                    style={{
                      width: "80px",
                      height: "80px",
                    }}
                  />
                </div>
              </>
            ) : (
              <CSOsFinanceRadar
                comparisonData={
                  rawComparisonData
                    ? removeKeyFromObject(comparisonData, "収益規模")
                    : defaultData
                }
              />
            )}
          </div>
        </Col>
        <Col
          xs={12}
          lg={6}
          style={{
            marginTop: "16px",
          }}
        >
          <div
            style={{
              border: "1px solid #E5E5E5",
              padding: "16px",
            }}
          >
            <div
              style={{
                fontWeight: "bold",
              }}
            >
              経年変化データ
            </div>
            {Object.keys(
              rawComparisonData
                ? moveLastPropertyToBeginning(comparisonData)
                : defaultData
            )
              .filter((key) => key !== "year")
              .map((label, idx) => {
                const active = activeColumnFilter === idx;
                return (
                  <button
                    style={{
                      fontSize: "12px",
                      marginBottom: "4px",
                      marginRight: "4px",
                      padding: "4px 12px",
                      border: "none",
                      color: "#fff",
                      backgroundColor: active ? "#44CAE1" : "#8D8D8D",
                      borderRadius: "5px",
                    }}
                    onClick={() => {
                      setActiveColumnFilter(idx);
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            {loadingComparisonData ? (
              <>
                <div
                  style={{
                    width: "100%",
                    height: "245px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <img
                    src={animationLogo}
                    alt="loading"
                    style={{
                      width: "80px",
                      height: "80px",
                    }}
                  />
                </div>
              </>
            ) : (
              <CsosFinanceColumns
                comparisonData={
                  rawComparisonData
                    ? moveLastPropertyToBeginning(comparisonData)
                    : defaultData
                }
                activeColumnFilter={activeColumnFilter}
              />
            )}
          </div>
        </Col>
      </Row>
      {/* )} */}
      <Modal
        isOpen={deleteModalOpen}
        toggle={toggle}
        backdropClassName={styles["reverted-backdrop"]}
        className={styles["modal-content"]}
      >
        <ModalHeader
          toggle={toggle}
          className={styles["modal-header"]}
        ></ModalHeader>
        <ModalBody
          style={{
            padding: "16px 40px 40px 40px",
          }}
        >
          <div
            style={{
              fontSize: "20px",
              fontWeight: "bold",
              textAlign: "center",
            }}
          >
            {`${selectedOrg?.cso?.name} を`}
            <br />
            <div>CSO連携リストから削除しますか</div>
          </div>
          <Button
            style={{
              width: "100%",
              backgroundColor: "#EB5757",
              border: "none",
              marginTop: "32px",
              padding: "13px 53px",
            }}
            onClick={handleDeleteCsoWish}
          >
            削除
          </Button>
        </ModalBody>
      </Modal>
    </Fragment>
  );
};

export default CompareCSO;
